import Popup from '../../components_v2/popup/Popup';
import * as React from 'react';
import { PopupMode, PopupProps } from '../../components_v2/popup/model/Model';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AAllowDenyList, AppliedAllowDenyList, AllowDenyList, PostAllowDenyList, postAllowDenyListMult, useAllowDenyList } from '../../atoms/allowDenyList';
import { AGroups } from '../../atoms/groups';
import { AUsers } from '../../atoms/global/users';
import PopupCreation from '../../components_v2/popup/PopupCreation';
import styled from 'styled-components';
import { PrimaryColor, BorderColor, DarkGreySidely2, RedSidely, SidelyBlack } from '../../styles/global/css/Utils';
import { FlexDiv } from '../products/style';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import AddIcon from '../../styles/custom-icons/add';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import { Trash } from 'lucide-react';
import { NoMarginH3, NoMarginH4, NoMarginP } from '../authorization/users/Members';
import { LoadingStateEnum } from '../import/model';
import { Translate, translateToString } from '../../styles/global/translate';
import { ObjectAction } from '../../web-types/objectActions';
import { capitalizeFirstLetter, OBJECT_ACTION_ACTIONS } from '../authorization/groups/PermissionTable';

function areMapEqual(map1: Map<ObjectAction, AppliedAllowDenyList>, map2: Map<ObjectAction, AppliedAllowDenyList>): boolean {
	// ensure we'er actually dealing with maps
	if (!(map1 instanceof Map) || !(map2 instanceof Map)) {
		return false;
	}
	// both maps have to have the same size
	if (map1.size !== map2.size) {
		return false;
	}
	// for every key in one map the same key with the same associated value must be present
	return [...map1.entries()].every(([key, value]) => (map2.has(key) && map2.get(key) == value));
}

export default function AllowDenyListPopup(props: Omit<PopupProps, 'children' | 'content'> & { onClose: () => void, targetId?: number, targetUuid?: string, objectActions: ObjectAction[], titles?: Partial<Record<ObjectAction, React.ReactNode>> }) {
	const appliedAllowDenyList = useAllowDenyList(props.objectActions, props.targetId, props.targetUuid);
	const [appliedState, setAppliedState] = React.useState<Map<ObjectAction, AppliedAllowDenyList>>(new Map(appliedAllowDenyList));
	const [_, setAtom] = useRecoilState(AAllowDenyList);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);


	React.useEffect(() => {
		setAppliedState(new Map(appliedAllowDenyList));
	}, [appliedAllowDenyList]);

	const onAdd = (currentObjectAction: ObjectAction, isDeny: boolean) => (isGroup: boolean) => (isGlobal: boolean, value: number) => {
		console.log('onAdd', currentObjectAction, isDeny, isGroup, isGlobal, value);
		setAppliedState(appliedState => {
			let key: keyof AppliedAllowDenyList;
			if (isDeny && isGlobal) {
				key = 'globalDenyList';
			} else if (isDeny) {
				key = 'localDenyList';
			} else if (isGlobal) {
				key = 'globalAllowList';
			} else {
				key = 'localAllowList';
			}
			const oldEntry = appliedState.get(currentObjectAction) ?? {};
			const entry: PostAllowDenyList = JSON.parse(JSON.stringify(oldEntry[key] ?? {
				target: currentObjectAction,
				target_id: isGlobal ? undefined : props.targetId,
				target_uuid: isGlobal ? undefined : props.targetUuid,
				deny: isDeny,
				users: [],
				groups: [],
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			}));
			if (isGroup) {
				entry.groups.push(value);
				entry.groups = [...new Set(entry.groups)];
			} else {
				entry.users.push(value);
				entry.users = [...new Set(entry.users)];
			}

			console.log(entry);

			const newMap = new Map(appliedState);
			newMap.set(currentObjectAction, { ...oldEntry, [key]: entry });
			return newMap;
		});
	};

	const onDelete = (currentObjectAction: ObjectAction, isDeny: boolean) => (isGroup: boolean) => (isGlobal: boolean, value: number) => {
		setAppliedState(appliedState => {
			let key: keyof AppliedAllowDenyList;
			if (isDeny && isGlobal) {
				key = 'globalDenyList';
			} else if (isDeny) {
				key = 'localDenyList';
			} else if (isGlobal) {
				key = 'globalAllowList';
			} else {
				key = 'localAllowList';
			}
			const oldEntry = appliedState.get(currentObjectAction) ?? {};
			if (!oldEntry[key]) return appliedState;
			let entry: PostAllowDenyList | undefined = { ...oldEntry[key]! };
			if (isGroup) {
				entry.groups = entry.groups.filter(g => g !== value);
			} else {
				entry.users = entry.users.filter(u => u !== value);
			}

			if (entry.users.length === 0 && entry.groups.length === 0 && entry.id === undefined) entry = undefined;

			const newMap = new Map(appliedState);
			newMap.set(currentObjectAction, { ...oldEntry, [key]: entry });
			return newMap;
		});
	};


	return <Popup onClickOut={props.onClose} popupMode={PopupMode.Details} {...props}>
		<PopupCreation
			title={<Translate id='allow_deny_list.restrictions' />}
			onClose={props.onClose}
			messageButton={translateToString('save')}
			canValidate={!areMapEqual(appliedState, appliedAllowDenyList)}
			onSubmit={() => {
				setLoadingState(LoadingStateEnum.LOADING);
				postAllowDenyListMult([...appliedState.values()].reduce<AllowDenyList[]>((acc, list) => {
					acc.push(...Object.values(list).filter((e) => !!e));
					return acc;
				}, []), setAtom).then(() => {
					setLoadingState(LoadingStateEnum.LOADED);
					props.onClose();
				});
			}}
		>
			<FlexDiv gap='10px' flow='column'>
				{
					props.objectActions.map(oa => {
						const customTitle = props.titles?.[oa];
						const action = OBJECT_ACTION_ACTIONS.find((a) => oa.startsWith(capitalizeFirstLetter(a)));
						if (!customTitle && !action) return null;
						const currentAppliedState = appliedState.get(oa);
						return <RestrictionContainer
							key={oa}
							title={customTitle ?? <Translate id={`permission.actions.${action}`} />}
							global={currentAppliedState?.globalDenyList}
							local={currentAppliedState?.localDenyList}
							onAdd={onAdd(oa, true)}
							onDelete={onDelete(oa, true)} />;
					})
				}
			</FlexDiv>
		</PopupCreation>
		<ComponentLoader loadingState={loadingState} />

	</Popup>;
}

function RestrictionContainer(props: {
	title: React.ReactNode,
	global: PostAllowDenyList | undefined,
	local: PostAllowDenyList | undefined,
	onAdd: (isGroup: boolean) => (isGlobal: boolean, value: number) => void,
	onDelete: (isGroup: boolean) => (isGlobal: boolean, value: number) => void,
}) {
	const groups = useRecoilValue(AGroups);
	const users = useRecoilValue(AUsers);
	const userDropdownData: DropdownData<number>[] = React.useMemo(() => users.map(u => ({ value: u.id, label: u.name })), [users]);
	const groupDropdownData: DropdownData<number>[] = React.useMemo(() => groups.map(g => ({ value: g.id, label: g.name })), [groups]);

	return <RestrictionContainerDiv>
		<NoMarginH3>{props.title}</NoMarginH3>
		<NoMarginH4><Translate id='users' /></NoMarginH4>
		<RestrictionList
			addText={translateToString('allow_deny_list.add_user')}
			global={props.global}
			local={props.local}
			data={users}
			target="users"
			emptyString={translateToString('allow_deny_list.no_user')}
			onAdd={props.onAdd(false)}
			onDelete={props.onDelete(false)}
			dropdownData={userDropdownData}
		/>
		<NoMarginH4><Translate id='admin.table.groups' /></NoMarginH4>
		<RestrictionList
			addText={translateToString('allow_deny_list.add_group')}
			global={props.global}
			local={props.local}
			data={groups}
			target="groups"
			emptyString={translateToString('allow_deny_list.no_group')}
			onAdd={props.onAdd(true)}
			onDelete={props.onDelete(true)}
			dropdownData={groupDropdownData}
		/>
	</RestrictionContainerDiv>;
}

function RestrictionList(props: {
	global: PostAllowDenyList | undefined,
	local: PostAllowDenyList | undefined,
	data: { name: string, id: number }[],
	target: 'users' | 'groups',
	emptyString: string,
	onAdd: (isGlobal: boolean, value: number) => void,
	onDelete: (isGlobal: boolean, value: number) => void,
	addText: string,
	dropdownData: DropdownData<number>[],
}) {
	return <>
		<ListItemWrapper>
			{props.local?.[props.target]?.map(data => <ListItem key={data}>
				<NoMarginP>{props.data.find(u => u.id === data)?.name}</NoMarginP>
				<SortSetting hoverColor={RedSidely}>
					<Trash onClick={() => props.onDelete(false, data)} />
				</SortSetting>
			</ListItem>)}
			<Dropdown
				name={'inner-sort-dd'}
				datalist={props.dropdownData}
				onChange={(value) => props.onAdd(false, value.value)}
				JSXButton={() => <SortSetting hoverColor={PrimaryColor}>
					<AddIcon size={16} />
					{props.addText}
				</SortSetting>}
				dropdownStyle={{ optionHeight: '250px', width: 'fit-content', containerWidth: 'fit-content' }}
			/>
		</ListItemWrapper>
	</>;


}

const Box = styled.div`
	padding: 10px;
	border: 1px solid ${BorderColor};
	border-radius: 5px;

`;

const RestrictionContainerDiv = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
`;

const ListItemWrapper = styled(Box)`
	margin-top: -5px;
`;

const ListItem = styled.div`
	&:before {
		content: '•';
		margin-right: 5px;
	}
	align-items: center;
	width: 100%;
	display: grid;
  	grid-template-columns: min-content 1fr min-content;
`;

const SortSetting = styled.div<{ hoverColor: string }>`
	cursor: pointer;
	border-radius: 5px;
	width: fit-content;
	padding: 5px;
  	display: flex;
   	align-items: center;
    gap: 5px;
    font-size: 12px;
    color: ${DarkGreySidely2};
    & > svg {
			color: ${DarkGreySidely2};
	}
	&:hover {
		color: ${p => p.hoverColor};
		background-color: color-mix(in oklab, ${SidelyBlack} 3%, transparent);
		& > svg {
			color: ${p => p.hoverColor};
		}
	}
`;
